import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Newspaper, Calendar, ExternalLink, Bell, Send, Inbox, FileText, Wallet, ShieldAlert } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { News as NewsType } from '@/types/legacy';
import { NewsFormDialog } from '@/components/News/NewsFormDialog';
import { EmployeeDocumentsList } from '@/components/Communication/EmployeeDocumentsList';
import { StaffAlertsList } from '@/components/Communication/StaffAlertsList';
import { AdminDocumentsPanel } from '@/components/Communication/AdminDocumentsPanel';
import { PayrollUploadPanel } from '@/components/Communication/PayrollUploadPanel';
import { PayrollDocumentsList } from '@/components/Communication/PayrollDocumentsList';
import { AnonymousReportForm, AnonymousReportTracker, AnonymousReportsAdmin } from '@/components/Communication';
import logger from '@/lib/logger';

export default function News() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [news, setNews] = useState<NewsType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState<NewsType | null>(null);
  const [pendingDocs, setPendingDocs] = useState(0);
  const [pendingReports, setPendingReports] = useState(0);
  const [anonymousFormOpen, setAnonymousFormOpen] = useState(false);

  const isAdmin = userRole === 'admin' || userRole === 'supervisor';

  const fetchNews = async () => {
    try {
      const { data, error } = await api.from('news').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setNews(data || []);
    } catch (error: any) {
      toast({ title: 'Error', description: 'No se pudieron cargar las noticias', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingDocsCount = async () => {
    try {
      const { data } = await api.from('employee_documents').select('id').eq('status', 'pending').eq('is_active', true);
      setPendingDocs(data?.length || 0);
    } catch (error) { logger.error('Error:', error); }
  };

  const fetchPendingReportsCount = async () => {
    try {
      const { data } = await api.from('anonymous_reports').select('id').eq('status', 'pending').eq('is_active', true);
      setPendingReports(data?.length || 0);
    } catch (error) { logger.error('Error:', error); }
  };

  const refreshNews = () => {
    fetchNews();
    if (isAdmin) {
      fetchPendingDocsCount();
      fetchPendingReportsCount();
    }
  };

  useEffect(() => {
    refreshNews();
  }, []);
  useDataRefresh(refreshNews, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const getPriorityBadge = (priority: number) => {
    if (priority >= 5) return { label: 'Alta', variant: 'destructive' as const };
    if (priority >= 3) return { label: 'Media', variant: 'default' as const };
    return { label: 'Baja', variant: 'secondary' as const };
  };

  const handleEdit = (newsItem: NewsType) => { setSelectedNews(newsItem); setDialogOpen(true); };
  const handleDelete = async (newsItem: any) => {
    if (!window.confirm(`¿Eliminar la noticia "${newsItem.title}"? Se marcará como inactiva y se conserva para trazabilidad.`)) return;
    const { error } = await api.from('news').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', newsItem.id);
    if (error) { toast({ title: 'Error', description: 'No se pudo eliminar: ' + error.message, variant: 'destructive' }); return; }
    toast({ title: 'Noticia eliminada', description: 'Marcada como inactiva (histórico conservado).' });
    fetchNews();
  };
  const handleNew = () => { setSelectedNews(null); setDialogOpen(true); };

  const NewsContent = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Tablon de Noticias</h3>
          <p className="text-sm text-muted-foreground">Anuncios y actualizaciones generales</p>
        </div>
        {isAdmin && <Button onClick={handleNew} size="sm"><Plus className="w-4 h-4 mr-2" />Nueva Noticia</Button>}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 text-center"><p className="text-2xl font-bold">{news.length}</p><p className="text-xs text-muted-foreground">Total</p></Card>
        <Card className="p-3 text-center"><p className="text-2xl font-bold text-green-500">{news.filter(n => n.is_active).length}</p><p className="text-xs text-muted-foreground">Activas</p></Card>
        <Card className="p-3 text-center"><p className="text-2xl font-bold text-red-500">{news.filter(n => n.priority >= 5).length}</p><p className="text-xs text-muted-foreground">Importantes</p></Card>
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div></div>
      ) : news.length === 0 ? (
        <Card className="p-12 text-center"><Newspaper className="w-16 h-16 mx-auto text-muted-foreground mb-4" /><p className="text-lg text-muted-foreground">No hay noticias publicadas</p></Card>
      ) : (
        <div className="space-y-4">
          {news.map((item) => {
            const priorityBadge = getPriorityBadge(item.priority);
            return (
              <Card key={item.id} className="p-4 hover:shadow-md transition-all">
                <div className="flex items-start gap-4">
                  {item.image_url && <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden"><img src={item.image_url} alt={item.title} className="w-full h-full object-cover" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h4 className="font-semibold">{item.title}</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant={priorityBadge.variant}>{priorityBadge.label}</Badge>
                        {!item.is_active && <Badge variant="secondary">Inactiva</Badge>}
                      </div>
                    </div>
                    {item.headline && <p className="text-sm font-medium text-primary mb-1">{item.headline}</p>}
                    {item.content && <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{item.content}</p>}
                    {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="w-3 h-3" />Ver mas</a>}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: es })}</span>
                      {isAdmin && <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>Editar</Button>}{isAdmin && item.is_active && <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(item)}>Eliminar</Button>}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">Comunicaciones</h1>
          <p className="text-muted-foreground mt-2">Noticias, alertas y documentos del personal</p>
        </div>
        <Tabs defaultValue="news" className="w-full">
          <TabsList className={`grid w-full h-auto ${isAdmin ? 'grid-cols-4 md:grid-cols-8' : 'grid-cols-3 md:grid-cols-5'}`}>
            <TabsTrigger value="news" className="flex items-center gap-2 py-3"><Newspaper className="w-4 h-4" /><span className="hidden sm:inline">Noticias</span></TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2 py-3"><Bell className="w-4 h-4" /><span className="hidden sm:inline">Alertas</span></TabsTrigger>
            <TabsTrigger value="payroll" className="flex items-center gap-2 py-3"><Wallet className="w-4 h-4" /><span className="hidden sm:inline">Nóminas</span></TabsTrigger>
            <TabsTrigger value="my-docs" className="flex items-center gap-2 py-3"><Send className="w-4 h-4" /><span className="hidden sm:inline">Solicitudes</span></TabsTrigger>
            <TabsTrigger value="anonymous" className="flex items-center gap-2 py-3"><ShieldAlert className="w-4 h-4" /><span className="hidden sm:inline">Buzón Anónimo</span></TabsTrigger>
            {isAdmin && <TabsTrigger value="inbox" className="flex items-center gap-2 py-3 relative"><Inbox className="w-4 h-4" /><span className="hidden sm:inline">Bandeja</span>{pendingDocs > 0 && <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">{pendingDocs}</Badge>}</TabsTrigger>}
            {isAdmin && <TabsTrigger value="reports-admin" className="flex items-center gap-2 py-3 relative"><ShieldAlert className="w-4 h-4" /><span className="hidden sm:inline">Reportes</span>{pendingReports > 0 && <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs">{pendingReports}</Badge>}</TabsTrigger>}
            {isAdmin && <TabsTrigger value="payroll-admin" className="flex items-center gap-2 py-3"><FileText className="w-4 h-4" /><span className="hidden sm:inline">Enviar Nóminas</span></TabsTrigger>}
          </TabsList>
          <TabsContent value="news" className="mt-6"><NewsContent /></TabsContent>
          <TabsContent value="alerts" className="mt-6"><StaffAlertsList viewMode={isAdmin ? 'admin' : 'employee'} /></TabsContent>
          <TabsContent value="payroll" className="mt-6"><PayrollDocumentsList /></TabsContent>
          <TabsContent value="my-docs" className="mt-6"><EmployeeDocumentsList /></TabsContent>
          <TabsContent value="anonymous" className="mt-6"><AnonymousReportTracker onNewReport={() => setAnonymousFormOpen(true)} /></TabsContent>
          {isAdmin && <TabsContent value="inbox" className="mt-6"><AdminDocumentsPanel /></TabsContent>}
          {isAdmin && <TabsContent value="reports-admin" className="mt-6"><AnonymousReportsAdmin /></TabsContent>}
          {isAdmin && <TabsContent value="payroll-admin" className="mt-6"><PayrollUploadPanel /></TabsContent>}
        </Tabs>
      </div>
      <NewsFormDialog open={dialogOpen} onOpenChange={setDialogOpen} news={selectedNews} onSuccess={fetchNews} />
      <AnonymousReportForm open={anonymousFormOpen} onOpenChange={setAnonymousFormOpen} />
    </DashboardLayout>
  );
}
